import React, {useEffect, useState} from 'react';
import {DataGrid, GridColDef, GridRowParams} from '@mui/x-data-grid';
import Modal from 'react-modal';
import {isRight} from 'fp-ts/lib/Either';
import UserDto from '../../interfaces/UserDto';
import PurchaseOrder from '../../interfaces/PurchaseOrder';
import ConsignmentDto from '../../interfaces/ConsignmentDto';
import ExpandedOrder from '../../interfaces/ExpandedOrder';
import {
    changePassword,
    fetchConsignmentsByUser,
    fetchOrderById,
    fetchOrdersByUser,
    makeUserAdmin,
    removeUserAdmin,
    setUserBanned,
    setUserRewards,
} from '../../api/endpoints';
import AdminOrderModal from './AdminOrderModal';
import AdminConsignmentModal from './AdminConsignmentModal';

interface Props {
    user: UserDto;
    closeModal: () => void;
    refetchUsers?: () => void;
}

const userColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 50, disableColumnMenu: true },
    { field: 'userFirstName', headerName: 'First Name', width: 120, disableColumnMenu: true },
    { field: 'userLastName', headerName: 'Last Name', width: 120, disableColumnMenu: true },
    { field: 'section', headerName: 'Section', width: 100, disableColumnMenu: true },
    { field: 'row', headerName: 'Row', width: 100, disableColumnMenu: true },
    { field: 'quantity', headerName: 'Quantity', width: 100, disableColumnMenu: true },
    { field: 'price', headerName: 'Price', width: 100, disableColumnMenu: true },
    { field: 'orderStatus', headerName: 'Order Status', width: 150, disableColumnMenu: true },
];

const consignmentColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 50, disableColumnMenu: true },
    { field: 'userFirstName', headerName: 'First Name', width: 100, disableColumnMenu: true },
    { field: 'userLastName', headerName: 'Last Name', width: 100, disableColumnMenu: true },
    { field: 'eventName', headerName: 'Event', width: 280, disableColumnMenu: true },
    { field: 'section', headerName: 'Section', width: 100, disableColumnMenu: true },
    { field: 'row', headerName: 'Row', width: 100, disableColumnMenu: true },
    { field: 'quantity', headerName: 'Qty', width: 100, disableColumnMenu: true },
    { field: 'status', headerName: 'Status', width: 100, disableColumnMenu: true }
];

const nestedModalStyle = {
    content: {
        display: 'flex' as const,
        flexDirection: 'column' as const,
        maxWidth: '1000px',
        margin: 'auto',
        top: '125px',
        bottom: '40px',
        padding: '40px',
        borderRadius: '15px',
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
    },
};

const AdminUserModal: React.FC<Props> = ({user: initialUser, closeModal, refetchUsers}) => {
    const [user, setUser] = useState<UserDto>(initialUser);
    const [userOrders, setUserOrders] = useState<PurchaseOrder[]>([]);
    const [userConsignments, setUserConsignments] = useState<ConsignmentDto[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<ExpandedOrder | null>(null);
    const [selectedConsignment, setSelectedConsignment] = useState<ConsignmentDto | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [newRewards, setNewRewards] = useState('');
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [loadingConsignments, setLoadingConsignments] = useState(false);
    const [loadingAction, setLoadingAction] = useState(false);

    useEffect(() => {
        setUser(initialUser);
    }, [initialUser]);

    const refetchOrders = async () => {
        setLoadingOrders(true);
        const ordersResponseEither = await fetchOrdersByUser(user.id);
        if (isRight(ordersResponseEither)) {
            setUserOrders(ordersResponseEither.right);
        } else {
            console.error(ordersResponseEither.left);
        }
        setLoadingOrders(false);
    };

    const refetchConsignments = async () => {
        setLoadingConsignments(true);
        const consignmentsResponseEither = await fetchConsignmentsByUser(user.id);
        if (isRight(consignmentsResponseEither)) {
            setUserConsignments(consignmentsResponseEither.right);
        } else {
            console.error(consignmentsResponseEither.left);
        }
        setLoadingConsignments(false);
    };

    useEffect(() => {
        refetchOrders();
        refetchConsignments();
    }, [user.id]);

    const handleAdminChange = async (id: number, isAdminAlready: boolean) => {
        setLoadingAction(true);
        const responseEither = isAdminAlready ? await removeUserAdmin(id) : await makeUserAdmin(id);
        if (isRight(responseEither)) {
            setUser(responseEither.right);
        }
        refetchUsers?.();
        setLoadingAction(false);
    };

    const handleBanChange = async (id: number, isBannedAlready: boolean) => {
        setLoadingAction(true);
        const responseEither = await setUserBanned({userId: id, banned: !isBannedAlready});
        if (isRight(responseEither)) {
            setUser(responseEither.right);
        }
        refetchUsers?.();
        setLoadingAction(false);
    };

    const handleNewRewardsSubmit = async (id: number) => {
        setLoadingAction(true);
        const responseEither = await setUserRewards({userId: id, newRewards: parseFloat(newRewards)});
        if (isRight(responseEither)) {
            setUser(responseEither.right);
        }
        refetchUsers?.();
        setLoadingAction(false);
    };

    const handleNewPasswordSubmit = async (id: number) => {
        if (newPassword !== '') {
            setLoadingAction(true);
            await changePassword({userId: id, newPassword: newPassword});
            setLoadingAction(false);
        }
    };

    const handleOrderClick = async (params: GridRowParams) => {
        const responseEither = await fetchOrderById(params.row.id);
        if (isRight(responseEither)) {
            setSelectedOrder(responseEither.right);
        } else {
            console.error(responseEither.left);
        }
    };

    const handleConsignmentClick = async (params: GridRowParams) => {
        setSelectedConsignment(params.row as ConsignmentDto);
    };

    return (
        <>
            <div>
                <div className='card-item'>
                    <div className='row'>
                        <button className='back-button' onClick={closeModal}>⬅</button>
                        <div className='text-m'>{"User Details (#" + user.id + "): "}</div>
                    </div>
                </div>
                <hr />
                {loadingAction ?
                    <div className="loading-spinner"></div>
                    :
                    <>
                        <div className='card-item'>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>First Name:&nbsp;</div>
                                <div className='text-xs-light'>{user.firstName}</div>
                            </div>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>Last Name:&nbsp;</div>
                                <div className='text-xs-light'>{user.lastName}</div>
                            </div>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>Email:&nbsp;</div>
                                <div className='text-xs-light'>{user.email}</div>
                            </div>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>Created At:&nbsp;</div>
                                <div className='text-xs-light'>{user.createdAt}</div>
                            </div>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>Address:&nbsp;</div>
                                <div className='text-xs-light'>{user.billAddress}</div>
                            </div>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>City:&nbsp;</div>
                                <div className='text-xs-light'>{user.billCity}</div>
                            </div>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>State:&nbsp;</div>
                                <div className='text-xs-light'>{user.billState}</div>
                            </div>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>Zip:&nbsp;</div>
                                <div className='text-xs-light'>{user.billZip}</div>
                            </div>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>Country:&nbsp;</div>
                                <div className='text-xs-light'>{user.billCountry}</div>
                            </div>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>Phone Number:&nbsp;</div>
                                <div className='text-xs-light'>{user.billPhoneNumber}</div>
                            </div>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>Heard About 714:&nbsp;</div>
                                <div className='text-xs-light'>{user.heardAbout}</div>
                            </div>
                        </div>
                        <hr />
                        <div className='card-item'>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>Rewards (${user.rewards.toFixed(2)}):&nbsp;</div>
                                <input type="number" className='admin-input' value={newRewards} onChange={(e) => setNewRewards(e.target.value)} style={{width: "200px"}}
                                    placeholder="Change in balance (+/-)"
                                />
                                <div className='gap-s' />
                                <button className='admin-action-button' onClick={() => handleNewRewardsSubmit(user.id)}>Change Rewards</button>
                            </div>
                        </div>
                        <hr />
                        <div className='card-item'>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>Admin:&nbsp;</div>
                                <div className='text-xs-light'>{user.isAdmin.toString()}</div>
                                <div className='gap-s' />
                                <button className='admin-action-button' onClick={() => handleAdminChange(user.id, user.isAdmin)}>{user.isAdmin ? "Remove Admin" : "Make Admin"}</button>
                            </div>
                            <br />
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>Banned:&nbsp;</div>
                                <div className='text-xs-light'>{user.banned.toString()}</div>
                                <div className='gap-s' />
                                <button className={`admin-${user.banned ? "action" : "delete"}-button`} onClick={() => handleBanChange(user.id, user.banned)}>{user.banned ? "Un-ban User" : "Ban User"}</button>
                            </div>
                        </div>
                        <hr />
                        <div className='card-item'>
                            <div className='row'>
                                <div className='text-xs-bold gap-xl'>Reset Password:&nbsp;</div>
                                <input type="text" className='admin-input' value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="New Password..."
                                />
                                <div className='gap-s' />
                                <button className='admin-action-button' onClick={() => handleNewPasswordSubmit(user.id)}>Change Password</button>
                            </div>
                        </div>
                        <hr />
                        <div className='card-item'>
                            <div className='text-xs-bold'>Orders: </div>
                        </div>
                        <div style={{height: 400, width: '100%'}}>
                            {loadingOrders ?
                                <div className="loading-spinner"></div>
                                :
                                <DataGrid
                                    rows={userOrders}
                                    columns={userColumns}
                                    getRowId={(row) => `${row.id}`}
                                    rowCount={userOrders.length}
                                    pageSizeOptions={[5, 10]}
                                    initialState={{
                                        pagination: {paginationModel: {pageSize: 5}},
                                    }}
                                    onRowClick={handleOrderClick}
                                    className='admin-data-grid'
                                />
                            }
                        </div>
                        <br />
                        <div className='card-item'>
                            <div className='text-xs-bold'>Consigments: </div>
                        </div>
                        <div style={{height: 400, width: '100%'}}>
                            {loadingConsignments ?
                                <div className="loading-spinner"></div>
                                :
                                <DataGrid
                                    rows={userConsignments}
                                    columns={consignmentColumns}
                                    getRowId={(row) => `${row.id}`}
                                    rowCount={userConsignments.length}
                                    pageSizeOptions={[5, 10]}
                                    initialState={{
                                        pagination: {paginationModel: {pageSize: 5}},
                                    }}
                                    onRowClick={handleConsignmentClick}
                                    className='admin-data-grid'
                                />
                            }
                        </div>
                    </>
                }
            </div>
            <Modal isOpen={selectedOrder !== null} onRequestClose={() => setSelectedOrder(null)} style={nestedModalStyle}
                appElement={document.getElementById('root') || undefined}
            >
                <>
                    {selectedOrder && (
                        <AdminOrderModal order={selectedOrder} refetchData={refetchOrders} closeModal={() => setSelectedOrder(null)} />
                    )}
                </>
            </Modal>
            <Modal isOpen={selectedConsignment !== null} onRequestClose={() => setSelectedConsignment(null)} style={nestedModalStyle}
                appElement={document.getElementById('root') || undefined}
            >
                <>
                    {selectedConsignment && (
                        <AdminConsignmentModal consignment={selectedConsignment} refetchData={refetchConsignments} closeModal={() => setSelectedConsignment(null)} />
                    )}
                </>
            </Modal>
        </>
    );
};

export default AdminUserModal;
